import React from 'react';
import { Form, Field } from 'react-final-form';
import TextInput from '../../../../components/UI/TextInput/TextInput';
import Select from '../../../../components/UI/Select/Select';
import Button from '../../../../components/UI/Button/Button';
import styles from './BenchmarkQuestionForm.module.scss';
import { BenchmarkQuestion } from '../../../../store/benchmark/types';
import { QueryType } from '../../../../types/QueryTypes';
import { TextArea } from '../../../../components/UI';

interface BenchmarkQuestionFormProps {
  initialValues?: Partial<BenchmarkQuestion>;
  onSubmit: (values: Partial<BenchmarkQuestion>) => Promise<void>;
  onCancel: () => void;
}

const BenchmarkQuestionForm: React.FC<BenchmarkQuestionFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const queryTypeOptions = Object.values(QueryType).map((type) => ({
    value: type,
    label: type,
  }));

  // Set default values, prioritizing initialValues if they exist
  const defaultValues = {
    query_type: QueryType.Generic,
    ...initialValues,
  };

  const validate = (values: Partial<BenchmarkQuestion>) => {
    const errors: Record<string, string> = {};
    if (!values.question) {
      errors.question = 'Question is required';
    }
    if (!values.query_type) {
      errors.query_type = 'Query type is required';
    }
    return errors;
  };

  return (
    <Form
      initialValues={defaultValues}
      onSubmit={onSubmit}
      validate={validate}
      render={({ handleSubmit, submitting, pristine, form }) => (
        <form onSubmit={handleSubmit} className={styles.form}>
          <Field name="question">
            {({ input, meta }) => (
              <TextArea
                {...input}
                label="Question"
                rows={4}
                error={meta.touched && meta.error ? meta.error : undefined}
                fullWidth
              />
            )}
          </Field>

          <Field name="query_type">
            {({ input, meta }) => (
              <Select<QueryType>
                {...input}
                label="Query Type"
                options={queryTypeOptions}
                error={meta.touched && meta.error ? meta.error : undefined}
              />
            )}
          </Field>

          <Field name="ground_truth">
            {({ input }) => (
              <TextInput
                {...input}
                label="Ground Truth"
                placeholder="Optional ground truth answer"
                fullWidth
              />
            )}
          </Field>

          <Field name="user_persona">
            {({ input }) => (
              <TextInput
                {...input}
                label="User Persona"
                placeholder="Optional user persona for filtering"
                fullWidth
              />
            )}
          </Field>

          <Field name="tests_for">
            {({ input }) => (
              <TextInput
                {...input}
                label="What it tests for"
                placeholder="Optional text to help you reason about the questions we're using"
                fullWidth
              />
            )}
          </Field>

          <div className={styles.buttons}>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                form.reset();
                onCancel();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || pristine} loading={submitting}>
              {initialValues?.id ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default BenchmarkQuestionForm;
